import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Fieldset, Input, Label, Paragraph, ValidationMessage } from '@digdir/designsystemet-react';
import type { ProposalRequest } from '../../../api/workEnvironmentTypes';
import type { FieldError } from '../../WorkEnvironmentSurveyDetail/assessmentView';
import { FormErrorSummary, TextField } from '../../WorkEnvironmentSurveyDetail/components/FormParts';
import {
  charactersLeft,
  MAX_TITLE_LENGTH,
  PROPOSAL_FIELDS,
  proposeErrorKey,
} from '../../WorkEnvironmentSurveyDetail/measuresView';
import classes from '../WorkEnvironmentTeamMeasures.module.css';

type Props = {
  areaName: string;
  proposal: ProposalRequest;
  errors: FieldError[];
  sending: boolean;
  failure: ReturnType<typeof proposeErrorKey> | null;
  onChange: (proposal: ProposalRequest) => void;
};

/** The title and reason, shown under the area the person chose */
export const ProposalFields = ({ areaName, proposal, errors, sending, failure, onChange }: Props): ReactElement => {
  const { t } = useTranslation();
  const errorFor = (fieldId: string) => errors.find((error) => error.fieldId === fieldId)?.message;

  return (
    <div className={classes.proposal}>
      <FormErrorSummary errors={errors} title={t('workEnvironmentMeasures.team.errorSummary')} />
      <Fieldset>
        <Fieldset.Legend>{t('workEnvironmentMeasures.team.measureQuestion', { area: areaName })}</Fieldset.Legend>
        <Field>
          <Label htmlFor={PROPOSAL_FIELDS.title}>{t('workEnvironmentMeasures.form.title')}</Label>
          <Field.Description id={`${PROPOSAL_FIELDS.title}-help`}>
            {t('workEnvironmentMeasures.form.titleHelp')}
          </Field.Description>
          <Input
            id={PROPOSAL_FIELDS.title}
            value={proposal.title}
            maxLength={MAX_TITLE_LENGTH}
            aria-describedby={`${PROPOSAL_FIELDS.title}-help ${PROPOSAL_FIELDS.title}-left`}
            aria-invalid={Boolean(errorFor(PROPOSAL_FIELDS.title)) || undefined}
            onChange={(event) => onChange({ ...proposal, title: event.target.value })}
          />
          <Paragraph data-size="xs" id={`${PROPOSAL_FIELDS.title}-left`}>
            {t('workEnvironmentMeasures.form.charactersLeft', { count: charactersLeft(proposal.title) })}
          </Paragraph>
          {errorFor(PROPOSAL_FIELDS.title) && <ValidationMessage>{errorFor(PROPOSAL_FIELDS.title)}</ValidationMessage>}
        </Field>
        <TextField
          id={PROPOSAL_FIELDS.reasonLength}
          label={t('workEnvironmentMeasures.form.reason')}
          help={t('workEnvironmentMeasures.form.reasonHelp')}
          value={proposal.reason}
          error={errorFor(PROPOSAL_FIELDS.reasonLength)}
          onChange={(reason) => onChange({ ...proposal, reason })}
        />
      </Fieldset>
      {failure && (
        <Alert data-color="danger">
          <Paragraph>
            {failure === 'closed' ? t('workEnvironmentMeasures.team.closed') : t('workEnvironmentMeasures.form.failed')}
          </Paragraph>
        </Alert>
      )}
      <div>
        <Button type="submit" variant="primary" loading={sending}>
          {t('workEnvironmentMeasures.team.send')}
        </Button>
      </div>
    </div>
  );
};
